import {
  EntityState,
  createSelector,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { api } from './api';
import { userApi } from './user';
import {
  AccessKeySecret,
  DefaultProductOption,
  LaunchOptions,
  LaunchSelection,
  Licence,
  Member,
  Namespace,
  NewOrganisation,
  NewServiceAccount,
  Organisation,
  ServiceAccount,
  UpdateOrganisation,
} from '@/types';

// Rows come from two systems (friend memberships, diplomat invitations)
// whose ids can collide, so the entity key is composite.
const memberKey = (m: Member) =>
  m.source === 'invitation' ? `invitation:${m.invitation.id}` : `user:${m.id}`;

const memberStatus = (m: Member) =>
  m.source === 'invitation' ? 'pending' : m.membership.status;

const memberEmail = (m: Member) =>
  m.source === 'invitation' ? m.invitation.email : m.email;

const members = createEntityAdapter<Member, string>({
  selectId: memberKey,
  sortComparer: (a: Member, b: Member) => {
    if (memberStatus(a) === 'pending' && memberStatus(b) === 'active') {
      return -1; // a should come first
    }

    if (memberStatus(a) === 'active' && memberStatus(b) === 'pending') {
      return 1; // b should come first
    }

    // If both have the same state, sort by email
    return memberEmail(a).localeCompare(memberEmail(b));
  },
});

const accounts = createEntityAdapter<ServiceAccount, number>({
  selectId: (a: ServiceAccount) => a.id,
  sortComparer: (a: ServiceAccount, b: ServiceAccount) =>
    a.name.localeCompare(b.name),
});

const organisations = createEntityAdapter<Organisation, number>({
  selectId: (o: Organisation) => o.id,
  sortComparer: (a: Organisation, b: Organisation) =>
    a.name.localeCompare(b.name),
});

const productLaunchSelectionsAdapter = createEntityAdapter<
  LaunchSelection,
  Namespace
>({
  selectId: (l: LaunchSelection) => l.namespace,
  sortComparer: (a: LaunchSelection, b: LaunchSelection) =>
    a.namespace.localeCompare(b.namespace),
});

const organisationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listOrganisations: builder.query<EntityState<Organisation, number>, void>({
      query: () => 'organisations',
      transformResponse: (response: any) =>
        organisations.setAll(
          organisations.getInitialState(),
          response.organisations,
        ),
    }),
    createOrganisation: builder.mutation<Organisation, NewOrganisation>({
      query: (request) => ({
        // FIX split registration organisation create so this can return the actual organisation
        // update listOrganisations
        url: `organisations`,
        method: 'POST',
        body: request,
      }),
      onQueryStarted: async (patch, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          organisationApi.util.updateQueryData(
            'listOrganisations',
            undefined,
            (current) => {
              organisations.setOne(current as any, data);
            },
          ),
        );
      },
    }),
    listMembers: builder.query<EntityState<Member, string>, number>({
      query: (id) => `organisations/${id}/members`,
      transformResponse: (response: any) =>
        members.setAll(members.getInitialState(), response.members),
    }),
    exportMembers: builder.mutation<{ filename: string; csv: string }, number>({
      query: (id) => ({
        url: `organisations/${id}/members/export`,
        method: 'POST',
      }),
    }),
    listServiceAccounts: builder.query<
      EntityState<ServiceAccount, number>,
      number
    >({
      query: (id) => `organisations/${id}/service-accounts`,
      transformResponse: (response: any) =>
        accounts.setAll(accounts.getInitialState(), response.serviceAccounts),
    }),

    listLicences: builder.query<Licence[], number>({
      query: (id) => `organisations/${id}/licences`,
      transformResponse: (response: any) => response.licences,
    }),

    updateOrganisation: builder.mutation<Organisation, UpdateOrganisation>({
      query: (organisation) => ({
        url: `organisations/${organisation.id}`,
        method: 'POST',
        body: organisation,
      }),
      onQueryStarted: async (patch, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listOrganisations',
            undefined,
            (current) => {
              organisations.updateOne(current as any, {
                id: patch.id,
                changes: patch,
              });
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            organisationApi.util.updateQueryData(
              'listOrganisations',
              undefined,
              (current) => {
                organisations.updateOne(current as any, {
                  id: data.id,
                  changes: data,
                });
              },
            ),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    revokeMembership: builder.mutation<void, RevokeAccessRequest>({
      query: ({ membershipId }) => ({
        url: `memberships/${membershipId}`,
        method: 'DELETE',
        body: {},
      }),
      onQueryStarted: async (
        { userId, organisationId, isMe },
        { dispatch, queryFulfilled },
      ) => {
        const patchResults: any[] = [];
        patchResults.push(
          dispatch(
            organisationApi.util.updateQueryData(
              'listMembers',
              organisationId,
              (current) => {
                members.removeOne(current as any, `user:${userId}`);
              },
            ),
          ),
        );
        if (isMe) {
          patchResults.push(
            dispatch(
              organisationApi.util.updateQueryData(
                'listOrganisations',
                undefined,
                (current) => {
                  organisations.removeOne(current as any, organisationId);
                },
              ),
            ),
          );
        }

        queryFulfilled.catch(() =>
          patchResults.forEach((patch) => patch.undo()),
        );
      },
    }),
    roleUpdate: builder.mutation<void, RoleUpdateRequest>({
      query: ({ organisationId, membershipId, role }) => ({
        url: `organisations/${organisationId}/members/${membershipId}/role`,
        method: 'PATCH',
        body: {
          role,
        },
      }),
      onQueryStarted: async (
        { userId, organisationId, role, isMe },
        { dispatch, queryFulfilled },
      ) => {
        const patchResults: any[] = [];
        patchResults.push(
          dispatch(
            organisationApi.util.updateQueryData(
              'listMembers',
              organisationId,
              (current) => {
                const member = members
                  .getSelectors()
                  .selectById(current, `user:${userId}`);
                if (member && member.source === 'membership') {
                  members.updateOne(current as any, {
                    id: `user:${userId}`,
                    changes: {
                      membership: {
                        ...member.membership,
                        permissions: {
                          editor: role === 'editor' ? true : false,
                        },
                      },
                    },
                  });
                }
              },
            ),
          ),
        );
        if (isMe) {
          patchResults.push(
            dispatch(
              userApi.util.updateQueryData('me', undefined, (current) => {
                const membership = current.memberships.find(
                  ({ organisationId: memberOrganisationId }) =>
                    organisationId === memberOrganisationId,
                );
                if (membership) {
                  membership.permissions = {
                    editor: role === 'editor' ? true : false,
                  };
                }
              }),
            ),
          );
        }

        queryFulfilled.catch(() =>
          patchResults.forEach((patch) => patch.undo()),
        );
      },
    }),

    cancelInvitation: builder.mutation<void, CancelInvitationRequest>({
      query: ({ membershipId }) => ({
        url: `memberships/${membershipId}`,
        method: 'DELETE',
        body: {},
      }),
      onQueryStarted: async (
        { userId, organisationId },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listMembers',
            organisationId,
            (current) => {
              members.removeOne(current as any, `user:${userId}`);
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    newInvitation: builder.mutation<Member[], NewInvitationRequest>({
      query: (newInvitations) => ({
        url: `invitations`,
        method: 'POST',
        body: newInvitations,
      }),
      onQueryStarted: async (
        { organisationId },
        { dispatch, queryFulfilled },
      ) => {
        const { data } = await queryFulfilled;
        dispatch(
          organisationApi.util.updateQueryData(
            'listMembers',
            organisationId,
            (current) => {
              members.setMany(current as any, data);
            },
          ),
        );
      },
    }),
    // Resend on a legacy friend pending row: the BFF cuts the row over to
    // diplomat and answers with the invitation row, which replaces the
    // membership row in the list.
    reissueInvitation: builder.mutation<Member, ReissueInvitationRequest>({
      query: ({ organisationId, membershipId }) => ({
        url: `organisations/${organisationId}/members/${membershipId}/reissue`,
        method: 'POST',
        body: {},
      }),
      onQueryStarted: async (
        { userId, organisationId },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listMembers',
            organisationId,
            (current) => {
              const member = members
                .getSelectors()
                .selectById(current, `user:${userId}`);
              if (member && member.source === 'membership') {
                members.updateOne(current as any, {
                  id: `user:${userId}`,
                  changes: {
                    membership: {
                      ...member.membership,
                      invitationExpired: false,
                    },
                  },
                });
              }
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            organisationApi.util.updateQueryData(
              'listMembers',
              organisationId,
              (current) => {
                members.removeOne(current as any, `user:${userId}`);
                members.setOne(current as any, data);
              },
            ),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    // Diplomat-issued organisation invitations, addressed by invitation id.
    cancelOrganisationInvitation: builder.mutation<
      void,
      OrganisationInvitationRef
    >({
      query: ({ invitationId }) => ({
        url: `invitations/${invitationId}`,
        method: 'DELETE',
        body: {},
      }),
      onQueryStarted: async (
        { organisationId, invitationId },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listMembers',
            organisationId,
            (current) => {
              members.removeOne(current as any, `invitation:${invitationId}`);
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    reissueOrganisationInvitation: builder.mutation<
      void,
      OrganisationInvitationRef
    >({
      query: ({ invitationId }) => ({
        url: `invitations/${invitationId}/reissue`,
        method: 'POST',
        body: {},
      }),
      onQueryStarted: async (
        { organisationId, invitationId },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listMembers',
            organisationId,
            (current) => {
              const member = members
                .getSelectors()
                .selectById(current, `invitation:${invitationId}`);
              if (member && member.source === 'invitation') {
                members.updateOne(current as any, {
                  id: `invitation:${invitationId}`,
                  changes: {
                    invitation: { ...member.invitation, expired: false },
                  },
                });
              }
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    organisationInvitationRoleUpdate: builder.mutation<
      void,
      OrganisationInvitationRoleUpdateRequest
    >({
      query: ({ invitationId, role }) => ({
        url: `invitations/${invitationId}/role`,
        method: 'PATCH',
        body: { role },
      }),
      onQueryStarted: async (
        { organisationId, invitationId, role },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listMembers',
            organisationId,
            (current) => {
              const member = members
                .getSelectors()
                .selectById(current, `invitation:${invitationId}`);
              if (member && member.source === 'invitation') {
                members.updateOne(current as any, {
                  id: `invitation:${invitationId}`,
                  changes: {
                    invitation: {
                      ...member.invitation,
                      permissions: {
                        editor: role === 'editor' ? true : false,
                      },
                    },
                  },
                });
              }
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    deleteServiceAccountAccessKey: builder.mutation<
      void,
      DeleteServiceAccountAccessKeyRequest
    >({
      query: ({ accountId, keyId }) => ({
        url: `service-accounts/${accountId}/keys/${keyId}`,
        method: 'DELETE',
        body: {},
      }),
      onQueryStarted: async (
        { organisationId, accountId, keyId },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listServiceAccounts',
            organisationId,
            (current) => {
              current.entities[accountId].keys = current.entities[
                accountId
              ]?.keys?.filter(({ id }) => id !== keyId);
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    newServiceAccountAccessKey: builder.mutation<
      AccessKeySecret,
      NewServiceAccountAccessKeyRequest
    >({
      query: ({ accountId, name, scopes, expiresAt }) => ({
        url: `service-accounts/${accountId}/keys`,
        method: 'POST',
        body: { name, scopes, expiresAt },
      }),
      onQueryStarted: async (
        { accountId, organisationId },
        { dispatch, queryFulfilled },
      ) => {
        const { data } = await queryFulfilled;
        dispatch(
          organisationApi.util.updateQueryData(
            'listServiceAccounts',
            organisationId,
            (current) => {
              current.entities[accountId]?.keys?.push({
                id: data.id,
                name: data.name,
                created: data.created,
                scopes: data.scopes,
                expiresAt: data.expiresAt,
              });
            },
          ),
        );
      },
    }),
    updateServiceAccountAccessKey: builder.mutation<
      { id: string; scopes: string[]; expiresAt?: string | null },
      UpdateServiceAccountAccessKeyRequest
    >({
      query: ({ accountId, keyId, scopes, expiresAt }) => ({
        url: `service-accounts/${accountId}/keys/${keyId}`,
        method: 'PATCH',
        body: { scopes, expiresAt },
      }),
      onQueryStarted: async (
        { organisationId, accountId, keyId, scopes, expiresAt },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listServiceAccounts',
            organisationId,
            (current) => {
              const key = current.entities[accountId]?.keys?.find(
                (k) => k.id === keyId,
              );
              if (key) {
                key.scopes = scopes;
                key.expiresAt = expiresAt;
              }
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    newServiceAccount: builder.mutation<
      ServiceAccount,
      NewServiceAccountRequest
    >({
      query: ({ organisationId, account }) => ({
        url: `organisations/${organisationId}/service-accounts`,
        method: 'POST',
        body: account,
      }),
      onQueryStarted: async (
        { organisationId },
        { dispatch, queryFulfilled },
      ) => {
        const { data } = await queryFulfilled;
        dispatch(
          organisationApi.util.updateQueryData(
            'listServiceAccounts',
            organisationId,
            (current) => {
              accounts.setOne(current as any, data);
            },
          ),
        );
      },
    }),
    updateServiceAccount: builder.mutation<
      ServiceAccount,
      UpdateServiceAccountRequest
    >({
      query: ({ accountId, description }) => ({
        url: `service-accounts/${accountId}`,
        method: 'PATCH',
        body: { description },
      }),
      onQueryStarted: async (
        { organisationId, accountId, description },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listServiceAccounts',
            organisationId,
            (current) => {
              const account = current.entities[accountId];
              if (account) {
                account.description = description;
              }
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    deleteServiceAccount: builder.mutation<void, DeleteServiceAccountRequest>({
      query: ({ accountId }) => ({
        url: `service-accounts/${accountId}`,
        method: 'DELETE',
        body: {},
      }),
      onQueryStarted: async (
        { organisationId, accountId },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listServiceAccounts',
            organisationId,
            (current) => {
              accounts.removeOne(current as any, accountId);
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    listServiceAccountOauthClients: builder.query<
      ServiceAccountOauthClient[],
      ServiceAccountRef
    >({
      query: ({ accountId }) => `service-accounts/${accountId}/oauth-clients`,
      transformResponse: (response: {
        oauthClients: ServiceAccountOauthClient[];
      }) => response.oauthClients,
    }),
    newServiceAccountOauthClient: builder.mutation<
      ServiceAccountOauthClientSecret,
      NewServiceAccountOauthClientRequest
    >({
      query: ({ accountId, name, redirectUris }) => ({
        url: `service-accounts/${accountId}/oauth-clients`,
        method: 'POST',
        body: { name, redirectUris },
      }),
      onQueryStarted: async ({ accountId }, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          organisationApi.util.updateQueryData(
            'listServiceAccountOauthClients',
            { accountId },
            (current) => {
              current.push({
                id: data.id,
                clientId: data.clientId,
                name: data.name,
                redirectUris: data.redirectUris,
                created: data.created,
                lastUsed: data.lastUsed,
              });
            },
          ),
        );
      },
    }),
    deleteServiceAccountOauthClient: builder.mutation<
      void,
      DeleteServiceAccountOauthClientRequest
    >({
      query: ({ accountId, clientId }) => ({
        url: `service-accounts/${accountId}/oauth-clients/${clientId}`,
        method: 'DELETE',
        body: {},
      }),
      onQueryStarted: async (
        { accountId, clientId },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'listServiceAccountOauthClients',
            { accountId },
            (current) => current.filter((c) => c.clientId !== clientId),
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    organisationDefaultProductOptions: builder.query<
      DefaultProductOption[],
      number
    >({
      query: (id) => `organisations/${id}/options/default/product`,
    }),
    organisationDefaultProduct: builder.query<DefaultProductOption, number>({
      query: (id) => `organisations/${id}/default/product`,
    }),
    organisationUpdateDefaultProduct: builder.mutation<
      DefaultProductOption,
      UpdateOrganisationDefaultProduct
    >({
      query: ({ id, preference }) => ({
        url: `organisations/${id}/default/product`,
        method: 'POST',
        body: preference,
      }),
      onQueryStarted: async (patch, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'organisationDefaultProduct',
            patch.id,
            (current) => {
              Object.assign(current, patch.preference);
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    organisationProductLaunchOptions: builder.query<LaunchOptions[], number>({
      query: (id) => `organisations/${id}/options/product/launch`,
    }),
    organisationProductLaunchSelections: builder.query<
      EntityState<LaunchSelection, Namespace>,
      number
    >({
      query: (id) => `organisations/${id}/product/launch`,
      transformResponse: (response: LaunchSelection[]) =>
        productLaunchSelectionsAdapter.setAll(
          productLaunchSelectionsAdapter.getInitialState(),
          response,
        ),
    }),
    organisationUpdateProductLaunchSelection: builder.mutation<
      LaunchSelection,
      UpdateLaunchSelection
    >({
      query: ({ id, selection }) => ({
        url: `organisation/${id}/product/launch/${selection.namespace}`,
        method: 'POST',
        body: selection.preference,
      }),
      onQueryStarted: async (patch, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          organisationApi.util.updateQueryData(
            'organisationProductLaunchSelections',
            patch.id,
            (current) => {
              productLaunchSelectionsAdapter.updateOne(current as any, {
                id: patch.selection.namespace,
                changes: patch.selection,
              });
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            organisationApi.util.updateQueryData(
              'organisationProductLaunchSelections',
              patch.id,
              (current) => {
                productLaunchSelectionsAdapter.updateOne(current as any, {
                  id: data.namespace,
                  changes: data,
                });
              },
            ),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

type RevokeAccessRequest = {
  organisationId: number;
  userId: number;
  membershipId: number;
  isMe: boolean;
};

type RoleUpdateRequest = {
  organisationId: number;
  userId: number;
  membershipId: number;
  role: string;
  isMe: boolean;
};

type CancelInvitationRequest = {
  organisationId: number;
  userId: number;
  membershipId: number;
};

type NewInvitationRequest = {
  invitees: { email: string; role: 'editor' | 'member' }[];
  organisationId: number;
};

type ReissueInvitationRequest = {
  organisationId: number;
  userId: number;
  membershipId: number;
};

type OrganisationInvitationRef = {
  organisationId: number;
  invitationId: number;
};

type OrganisationInvitationRoleUpdateRequest = {
  organisationId: number;
  invitationId: number;
  role: 'editor' | 'member';
};

type NewServiceAccountRequest = {
  organisationId: number;
  account: NewServiceAccount;
};

type UpdateServiceAccountRequest = {
  organisationId: number;
  accountId: number;
  description?: string | null;
};

type DeleteServiceAccountRequest = {
  organisationId: number;
  accountId: number;
};

type NewServiceAccountAccessKeyRequest = {
  organisationId: number;
  accountId: number;
  name?: string;
  scopes?: string[];
  expiresAt?: string | null;
};

type UpdateServiceAccountAccessKeyRequest = {
  organisationId: number;
  accountId: number;
  keyId: string;
  scopes: string[];
  expiresAt?: string | null;
};

type DeleteServiceAccountAccessKeyRequest = {
  organisationId: number;
  accountId: number;
  keyId: string;
};

type ServiceAccountRef = {
  accountId: number;
};

type ServiceAccountOauthClient = {
  id: number;
  clientId: string;
  name: string | null;
  redirectUris: string[];
  created: string;
  lastUsed: string | null;
};

type ServiceAccountOauthClientSecret = ServiceAccountOauthClient & {
  clientSecret: string;
};

type NewServiceAccountOauthClientRequest = {
  accountId: number;
  name?: string;
  redirectUris: string[];
};

type DeleteServiceAccountOauthClientRequest = {
  accountId: number;
  clientId: string;
};

type UpdateOrganisationDefaultProduct = {
  id: number;
  preference: DefaultProductOption;
};

type UpdateLaunchSelection = {
  id: number;
  selection: LaunchSelection;
};

const organisationEntitiesSelector = createSelector(
  organisationApi.endpoints.listOrganisations.select(),
  (result) => result?.data ?? organisations.getInitialState(),
);

export { organisationEntitiesSelector };

export const {
  selectById: organisationByIdSelector,
  selectIds: organisationIdsSelector,
  selectAll: organisationsSelector,
} = organisations.getSelectors(organisationEntitiesSelector);

export const membersSelector = (organisationId: number) => {
  const selector = createSelector(
    organisationApi.endpoints.listMembers.select(organisationId),
    (result) => result?.data ?? members.getInitialState(),
  );
  return members.getSelectors(selector).selectAll;
};

export const accountsSelector = (organisationId: number) => {
  const selector = createSelector(
    organisationApi.endpoints.listServiceAccounts.select(organisationId),
    (result) => result?.data ?? accounts.getInitialState(),
  );
  return accounts.getSelectors(selector).selectAll;
};

export const organisationProductLaunchSelectionsSelector = (
  organisationId: number,
) => {
  const selector = createSelector(
    organisationApi.endpoints.organisationProductLaunchSelections.select(
      organisationId,
    ),
    (result) =>
      result?.data ?? productLaunchSelectionsAdapter.getInitialState(),
  );
  return productLaunchSelectionsAdapter.getSelectors(selector).selectAll;
};

export const {
  useListOrganisationsQuery,
  useUpdateOrganisationMutation,
  useCreateOrganisationMutation,
  useRevokeMembershipMutation,
  useExportMembersMutation,
  useCancelInvitationMutation,
  useNewInvitationMutation,
  useReissueInvitationMutation,
  useCancelOrganisationInvitationMutation,
  useReissueOrganisationInvitationMutation,
  useOrganisationInvitationRoleUpdateMutation,
  useRoleUpdateMutation,
  useListMembersQuery,
  useListServiceAccountsQuery,
  useListLicencesQuery,
  useNewServiceAccountAccessKeyMutation,
  useNewServiceAccountMutation,
  useOrganisationDefaultProductOptionsQuery,
  useOrganisationDefaultProductQuery,
  useOrganisationUpdateDefaultProductMutation,
  useOrganisationProductLaunchOptionsQuery,
  useOrganisationProductLaunchSelectionsQuery,
  useOrganisationUpdateProductLaunchSelectionMutation,
  useDeleteServiceAccountAccessKeyMutation,
  useDeleteServiceAccountMutation,
  useUpdateServiceAccountAccessKeyMutation,
  useUpdateServiceAccountMutation,
  useListServiceAccountOauthClientsQuery,
  useNewServiceAccountOauthClientMutation,
  useDeleteServiceAccountOauthClientMutation,
} = organisationApi;

export type { ServiceAccountOauthClient, ServiceAccountOauthClientSecret };
